import { EmailSendModal, MileageModal, SharedTable } from '@components';
import { CalendarOutlined, DashboardOutlined, EyeOutlined, MailOutlined, ToolOutlined } from '@ant-design/icons';
import { IServiceHistoriesHook } from '@models';
import { ServiceHistory } from '@services';
import * as Services from '@services';
import { Button, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useView } from '@hooks';
import { observer } from 'mobx-react';
import { Columns } from './VehicleHistoryTable.columns';

interface IProps {
  open: boolean;
  customerId?: string;
  historyHook: IServiceHistoriesHook;
  serviceHistory: ServiceHistory | null;
  handleClose: () => void;
  setOpen: (open: boolean) => void;
  handleOpen: (serviceHistory: ServiceHistory) => void;
  emailModalOpen: boolean;
  email: string;
  emailError: string | null;
  emailSending: boolean;
  handleEmailOpen: (serviceHistory: ServiceHistory) => void;
  handleEmailCancel: () => void;
  handleEmailChange: (val: string) => void;
  handleEmailSend: () => void;
  compact?: boolean;
}

const getTenantId = () => {
  try {
    // @ts-ignore
    return typeof abp !== 'undefined' ? abp.session.tenantId : undefined;
  } catch {
    return undefined;
  }
};

const PAGE_SIZE = 10;

const CompactHistoryList: React.FC<{
  historyHook: IServiceHistoriesHook;
  handleOpen: (serviceHistory: ServiceHistory) => void;
  handleEmailOpen: (serviceHistory: ServiceHistory) => void;
}> = ({ historyHook, handleOpen, handleEmailOpen }) => {
  const [items, setItems] = useState<ServiceHistory[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const skipRef = useRef(0);
  const vinRef = useRef('');

  useEffect(() => {
    // Initial load from hook data
    const incoming = historyHook?.dataSource ?? [];
    if (incoming.length > 0) {
      setItems(incoming);
      setTotalCount(historyHook.totalCount);
      skipRef.current = incoming.length;
      vinRef.current = incoming[0].vin;
    }
  }, [historyHook.dataSource, historyHook.totalCount]);

  const hasMore = items.length < totalCount;

  const handleLoadMore = async () => {
    if (!vinRef.current || loading) return;
    setLoading(true);
    try {
      const result = await Services.ServiceHistoryService.getAll({
        vin: vinRef.current,
        skipCount: skipRef.current,
        maxResultCount: PAGE_SIZE,
      });
      setItems((prev) => [...prev, ...result.items]);
      setTotalCount(result.totalCount);
      skipRef.current += result.items.length;
    } finally {
      setLoading(false);
    }
  };

  if (!items.length && !historyHook.loading) {
    return <div style={{ textAlign: 'center', color: '#8c8c8c', padding: 24 }}>No service history</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((history: ServiceHistory) => {
        const services = [
          ...new Set((history.reminderServices ?? []).map((s) => s.reminderName).filter(Boolean)),
        ];
        const tenantId = getTenantId();
        const allowUpdate =
          tenantId !== undefined &&
          history.tenantId === tenantId &&
          history.creationTime &&
          dayjs().diff(dayjs(history.creationTime), 'month', true) < 1;

        return (
          <div
            key={history.id}
            style={{
              background: '#fff',
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: '10px 14px',
            }}
          >
            {/* Header: date + actions */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CalendarOutlined style={{ color: '#2CBDFF', fontSize: 13 }} />
                <strong style={{ fontSize: 13 }}>
                  {dayjs(history.creationTime).format('DD/MM/YYYY')}
                </strong>
                {history.mileage > 0 && (
                  <Tag style={{ margin: 0, fontSize: 11 }} title="Mileage">
                    <DashboardOutlined /> {history.mileage.toLocaleString()} mi
                  </Tag>
                )}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() =>
                    allowUpdate
                      ? handleOpen(history)
                      : window.open(`/service-history/${history.vin}`, '_blank')
                  }
                >
                  Preview
                </Button>
                <Button
                  size="small"
                  type="primary"
                  style={{ background: '#52c41a', borderColor: '#52c41a' }}
                  icon={<MailOutlined />}
                  onClick={() => handleEmailOpen(history)}
                >
                  Email
                </Button>
              </div>
            </div>

            {/* Services */}
            {services.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {services.map((name, i) => (
                  <Tag
                    key={i}
                    icon={<ToolOutlined />}
                    style={{
                      margin: 0,
                      fontSize: 12,
                      borderRadius: 4,
                      background: '#f0f7ff',
                      borderColor: '#d4e4f7',
                      color: '#1a3353',
                    }}
                  >
                    {name}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {hasMore && (
        <Button
          block
          type="text"
          loading={historyHook.loading}
          onClick={handleLoadMore}
          style={{ marginTop: 4, color: '#2CBDFF', fontWeight: 500 }}
        >
          Load more ({items.length} of {historyHook.totalCount})
        </Button>
      )}
    </div>
  );
};

const VehicleHistoryTable: React.FC<IProps> = ({
  open,
  customerId,
  historyHook,
  serviceHistory,
  setOpen,
  handleOpen,
  handleClose,
  emailModalOpen,
  email,
  emailError,
  emailSending,
  handleEmailOpen,
  handleEmailCancel,
  handleEmailChange,
  handleEmailSend,
  compact = false,
}) => {
  const { isMobile } = useView();

  const handleNavigateToHistory = () => {
    const vin = historyHook?.dataSource?.[0]?.vin;
    if (vin) {
      window.open(`/service-history/${vin}`, '_blank');
    }
  };

  const hasServiceHistory = historyHook?.dataSource && historyHook.dataSource.length > 0;

  return (
    <>
      {compact ? (
        <>
          <CompactHistoryList
            historyHook={historyHook}
            handleOpen={handleOpen}
            handleEmailOpen={handleEmailOpen}
          />
          {serviceHistory && (
            <MileageModal
              open={open}
              id={serviceHistory.id}
              orderId={serviceHistory.orderId}
              mileage={serviceHistory.mileage}
              vin={serviceHistory.vin}
              customerId={customerId}
              onCancel={handleClose}
              setOpen={setOpen}
            />
          )}
          <EmailSendModal
            open={emailModalOpen}
            title="Send service history email"
            emailValue={email}
            setEmailValue={handleEmailChange}
            emailError={emailError}
            onConfirm={handleEmailSend}
            onCancel={handleEmailCancel}
            confirmText="Send"
            okButtonProps={{ disabled: !!emailError, loading: emailSending }}
          />
        </>
      ) : isMobile ? (
        <Button onClick={handleNavigateToHistory} disabled={!hasServiceHistory} type="primary">
          See service history
        </Button>
      ) : (
        <>
          <SharedTable
            {...historyHook}
            columns={Columns({ handleOpen, handleEmailOpen, iconOnly: compact })}
            summary={() => (
              <Table.Summary fixed="top">
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} />
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
          {serviceHistory && (
            <MileageModal
              open={open}
              id={serviceHistory.id}
              orderId={serviceHistory.orderId}
              mileage={serviceHistory.mileage}
              vin={serviceHistory.vin}
              customerId={customerId}
              onCancel={handleClose}
              setOpen={setOpen}
            />
          )}
          <EmailSendModal
            open={emailModalOpen}
            title="Send service history email"
            emailValue={email}
            setEmailValue={handleEmailChange}
            emailError={emailError}
            onConfirm={handleEmailSend}
            onCancel={handleEmailCancel}
            confirmText="Send"
            okButtonProps={{ disabled: !!emailError, loading: emailSending }}
          />
        </>
      )}
    </>
  );
};

export default observer(VehicleHistoryTable);
